import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import SortableTab from 'components/AppMainNavigationTabBar/SortableTab';
import AppPaperBox from 'components/AppPaperBox';
import { memo, MouseEvent, useCallback, useEffect, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AppHistoryStorageKey, routes } from 'routes/constants';

export type RouteHistoryItem = {
  pathname: string;
  search: string;
  hash: string;
  title: string;
  pathSegmentTitle?: string;
};

const AppMainNavigationTabBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [history, setHistory] = useState<RouteHistoryItem[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 },
    })
  );

  const getTabTitleFromPathName = useCallback(
    (pathname: string) =>
      routes.find(({ path }) => matchPath(path, pathname))?.title ?? '',
    []
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = localStorage.getItem(AppHistoryStorageKey);
      if (!raw) return;

      const parsed = JSON.parse(raw) as RouteHistoryItem[];
      if (!Array.isArray(parsed)) return;

      setHistory(parsed);
    } catch {
      localStorage.removeItem(AppHistoryStorageKey);
    }
  }, []);

  useEffect(() => {
    const current = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      title: getTabTitleFromPathName(location.pathname),
    };

    setHistory(prev => {
      const isSameRoute = prev.some(
        r =>
          r.pathname === current.pathname &&
          r.search === current.search &&
          r.hash === current.hash
      );

      if (isSameRoute) return prev;

      const titleIndex = prev.findIndex(r => r.title === current.title);

      if (titleIndex !== -1) {
        const next = [...prev];
        next[titleIndex] = current;
        return next;
      }

      return [...prev, current];
    });
  }, [
    location.pathname,
    location.search,
    location.hash,
    getTabTitleFromPathName,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem(AppHistoryStorageKey, JSON.stringify(history));
    } catch {}
  }, [history]);

  const handleRemoveTab = useCallback(
    (e: MouseEvent<HTMLButtonElement>, pathName: string) => {
      e.stopPropagation();
      setHistory(prev => prev.filter(r => r.pathname !== pathName));
    },
    []
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id === over.id) return;

    setHistory(prev => {
      const oldIndex = prev.findIndex(i => i.pathname === String(active.id));
      const newIndex = prev.findIndex(i => i.pathname === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const currentURL = `${location.pathname}${location.search ? location.search : ''}${location.hash ? location.hash : ''}`;

  return (
    <AppPaperBox className="mb-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={history.map(h => h.pathname)}
          strategy={horizontalListSortingStrategy}
        >
          <ul className="flex items-center gap-x-1.5 overflow-x-auto overflow-y-hidden p-2 text-xs">
            {history.map(route => {
              const tabURL = `${route.pathname}${route.search ? route.search : ''}${route.hash ? route.hash : ''}`;
              const isActiveTab = tabURL === currentURL;

              return (
                <SortableTab
                  key={route.pathname}
                  route={route}
                  tabURL={tabURL}
                  isActiveTab={isActiveTab}
                  onNavigate={navigate}
                  onRemove={handleRemoveTab}
                />
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
    </AppPaperBox>
  );
};

export default memo(AppMainNavigationTabBar);
